import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.jsx';
import Footer from '../common/Footer.jsx';
import '../../styles/homepage.css';
import logo from '../../images/logo.webp';


const Homepage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Navbar scroll effect
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);

    // Enhanced Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in');
          
          // Add staggered animation for feature highlights
          if (entry.target.classList.contains('hero-features')) {
            const items = entry.target.querySelectorAll('.feature-highlight-item');
            items.forEach((item, index) => {
              setTimeout(() => {
                item.style.animation = `featuresSlideUp 0.6s ease-out forwards`;
                item.style.animationDelay = `${index * 0.1}s`;
              }, index * 100);
            });
          }
          
          // Add special animation for zigzag features
          if (entry.target.classList.contains('zigzag-feature')) {
            const featureContent = entry.target.querySelector('.feature-content');
            const featureVisual = entry.target.querySelector('.feature-visual');
            
            if (featureContent) {
              featureContent.style.animation = 'enhancedFadeIn 0.8s ease-out forwards';
            }
            if (featureVisual) {
              featureVisual.style.animation = 'enhancedFadeIn 0.8s ease-out 0.2s forwards';
            }
          }
        }
      });
    }, observerOptions);

    const elementsToObserve = document.querySelectorAll('.fade-element');
    elementsToObserve.forEach(el => observer.observe(el));

    // Add parallax effect
    const handleParallax = () => {
      const scrolled = window.pageYOffset;
      const parallaxElements = document.querySelectorAll('.gradient-orb');
      
      parallaxElements.forEach((el, index) => {
        const speed = 0.5 + (index * 0.1);
        el.style.transform = `translateY(${scrolled * speed}px)`;
      });
    };

    window.addEventListener('scroll', handleParallax);

    // Add cursor trail effect
    let mouseX = 0;
    let mouseY = 0;
    let trail = [];

    const createTrailParticle = (x, y) => {
      const particle = document.createElement('div');
      particle.className = 'cursor-trail-particle';
      particle.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        width: 4px;
        height: 4px;
        background: rgba(0, 212, 255, 0.6);
        border-radius: 50%;
        pointer-events: none;
        z-index: 9999;
        animation: trailFade 1s ease-out forwards;
      `;
      
      document.body.appendChild(particle);
      
      setTimeout(() => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      }, 1000);
    };

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      
      // Only create trail particles on homepage
      if (window.location.pathname === '/' || window.location.pathname === '') {
        if (Math.random() > 0.8) { // Reduce frequency
          createTrailParticle(mouseX, mouseY);
        }
      }
    };

    // Add CSS for trail particles
    const trailStyles = document.createElement('style');
    trailStyles.textContent = `
      @keyframes trailFade {
        0% {
          opacity: 1;
          transform: scale(1);
        }
        100% {
          opacity: 0;
          transform: scale(0);
        }
      }
    `;
    document.head.appendChild(trailStyles);

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleParallax);
      document.removeEventListener('mousemove', handleMouseMove);
      observer.disconnect();
      
      // Clean up trail styles
      if (trailStyles.parentNode) {
        trailStyles.parentNode.removeChild(trailStyles);
      }
    };
  }, []);

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="homepage">
      {/* Homepage Header */}
      <header className="homepage-header">
        <div className="homepage-nav">
          {/* Logo on the left */}
          <Link to="/" className="homepage-logo">
            <img src={logo} alt="DevRoom Logo" className="homepage-logo-img" />
            <span className="logo-text">DevRoom</span>
          </Link>

          {/* Auth Buttons on the right */}
          <div className="homepage-auth-buttons">
            {user ? (
              <>
                <Link to="/dashboard" className="btn-primary-nav">Dashboard</Link>
                
              </>
            ) : (
              <>
                <Link to="/login" className="btn-primary-nav">Login</Link>
                <Link to="/register" className="btn-primary-nav">Register</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-container">
          <div className="hero-content-center">
            <div className="hero-text-center">
              <h2 className="hero-title fade-element" style={{ fontSize: 'clamp(2.2rem, 8vw, 6.4rem)' }}>
                <span className="title-line" style={{whiteSpace: 'nowrap'}}>Where Collaboration</span>
              <span className="gradient-text-animated meets-code-tight" style={{ fontSize: 'clamp(2.2rem, 5vw, 3.2rem)' }}>Meets Code</span>
              </h2>
              <p className="hero-description fade-element">
                DevRoom is your all-in-one collaborative coding environment, enabling real-time teamwork in a VS Code-powered editor. Whether you're across the room or across the globe, DevRoom brings your team together to brainstorm, build, and debug faster and smarter. Experience seamless pair programming, instant code sharing, and a unified workspace designed for modern development teams.
              </p>
              <div className="hero-actions fade-element">
                <button onClick={handleGetStarted} className="homepage-getstarted-btn">
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-bg-effects">
          <div className="grid-overlay"></div>
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
          <div className="particle-system">
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
          </div>
        </div>
      </section>

      {/* Features Section with Zigzag Layout */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header fade-element">
            <h2>Why Choose DevRoom?</h2>
            <p className="section-subtitle">Experience the future of collaborative development</p>
          </div>
          
          <div className="zigzag-features">
            {/* Feature 1 - Left aligned */}
            <div className="zigzag-feature fade-element" data-animation="slide-left" style={{marginLeft: 'auto', marginRight: 'auto'}}>
              <div className="feature-content">
                <div className="feature-icon">
                  <i className="fas fa-users"></i>
                  <div className="icon-glow"></div>
                </div>
                <h3>Real-time Collaboration</h3>
                <p>
                  Work together in real-time with live cursors, instant sync, and
                  seamless multi-user editing. See your teammates' changes as they happen
                  and collaborate like never before.
                </p>
                <div className="feature-highlights">
                  <span className="highlight">Live Cursors</span>
                  <span className="highlight">Instant Sync</span>
                  <span className="highlight">Multi-user Editing</span>
                </div>
              </div>
              <div className="feature-visual">
                <div className="visual-card">
                  <div className="collaboration-demo">
                    <div className="collab-header">
                      <div className="collab-status">
                        <div className="status-dot"></div>
                        <span>Live Collaboration</span>
                      </div>
                    </div>
                    
                    <div className="code-workspace">
                      <div className="code-editor-collab">
                        <div className="editor-line">
                          <span className="line-num">1</span>
                          <span className="code-text">function <span className="function">updateUI</span>() {'{'}</span>
                          <div className="cursor cursor-blue"></div>
                        </div>
                        <div className="editor-line">
                          <span className="line-num">2</span>
                          <span className="code-text">  const element = document.getElementById('app');</span>
                          <div className="cursor cursor-green"></div>
                        </div>
                        <div className="editor-line">
                          <span className="line-num">3</span>
                          <span className="code-text">  element.textContent = 'Hello World!';</span>
                          <div className="cursor cursor-purple"></div>
                        </div>
                        <div className="editor-line">
                          <span className="line-num">4</span>
                          <span className="code-text">{'}'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="cursor-legend">
                      <div className="cursor-info">
                        <div className="cursor-dot blue"></div>
                        <span>User 1</span>
                      </div>
                      <div className="cursor-info">
                        <div className="cursor-dot green"></div>
                        <span>User 2</span>
                      </div>
                      <div className="cursor-info">
                        <div className="cursor-dot purple"></div>
                        <span>User 3</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 2 - Right aligned */}
            <div className="zigzag-feature reverse fade-element" data-animation="slide-right" style={{marginLeft: 'auto', marginRight: 'auto'}}>
              <div className="feature-content">
                <div className="feature-icon">
                  <i className="fab fa-microsoft"></i>
                  <div className="icon-glow"></div>
                </div>
                <h3>VS Code Integration</h3>
                <p>
                  Native VS Code experience with all your favorite extensions and
                  themes, enhanced for collaboration. Keep your workflow while
                  gaining powerful team features.
                </p>
                <div className="feature-highlights">
                  <span className="highlight">Native Experience</span>
                  <span className="highlight">Extension Support</span>
                  <span className="highlight">Custom Themes</span>
                </div>
              </div>
              <div className="feature-visual">
                <div className="visual-card">
                  <div className="vscode-demo">
                    <div className="vscode-header">
                      <div className="vscode-tabs">
                        <div className="tab active">main.js</div>
                        <div className="tab">style.css</div>
                      </div>
                    </div>
                    <div className="vscode-content">
                      <div className="code-line">
                        <span className="line-number">1</span>
                        <span className="code">
                          <span className="keyword">import</span> <span className="string">'./styles.css'</span>;
                        </span>
                      </div>
                      <div className="code-line">
                        <span className="line-number">2</span>
                        <span className="code"></span>
                      </div>
                      <div className="code-line">
                        <span className="line-number">3</span>
                        <span className="code">
                          <span className="keyword">function</span> <span className="function-name">DevRoomApp</span>() {'{'}
                        </span>
                      </div>
                      <div className="code-line">
                        <span className="line-number">4</span>
                        <span className="code">
                          &nbsp;&nbsp;<span className="keyword">const</span> <span className="variable">users</span> = <span className="string">'online'</span>;
                        </span>
                      </div>
                      <div className="code-line">
                        <span className="line-number">5</span>
                        <span className="code">
                          &nbsp;&nbsp;<span className="keyword">return</span> &lt;<span className="keyword">div</span>&gt;Hello DevRoom!&lt;/<span className="keyword">div</span>&gt;;
                        </span>
                      </div>
                      <div className="code-line">
                        <span className="line-number">6</span>
                        <span className="code">{'}'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature 3 - Left aligned */}
            <div className="zigzag-feature fade-element" data-animation="slide-left" style={{marginLeft: 'auto', marginRight: 'auto'}}>
              <div className="feature-content">
                <div className="feature-icon">
                  <i className="fas fa-cloud"></i>
                  <div className="icon-glow"></div>
                </div>
                <h3>Cloud Workspace</h3>
                <p>
                  Access your development environment from anywhere with cloud-based
                  workspaces. No more "it works on my machine" – everyone shares
                  the same environment.
                </p>
                <div className="feature-highlights">
                  <span className="highlight">Cloud Based</span>
                  <span className="highlight">Access Anywhere</span>
                  <span className="highlight">Consistent Environment</span>
                </div>
              </div>
              <div className="feature-visual">
                <div className="visual-card">
                  <div className="cloud-demo">
                    <div className="cloud-icon">
                      <i className="fas fa-cloud"></i>
                    </div>
                    <div className="devices">
                      <div className="device laptop">
                        <i className="fas fa-laptop"></i>
                      </div>
                      <div className="device tablet">
                        <i className="fas fa-tablet-alt"></i>
                      </div>
                      <div className="device mobile">
                        <i className="fas fa-mobile-alt"></i>
                      </div>
                    </div>
                    <div className="sync-lines">
                      <div className="sync-line line-1"></div>
                      <div className="sync-line line-2"></div>
                      <div className="sync-line line-3"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            
          </div>
        </div>
        <div className="features-bg">
          <div className="feature-orb"></div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Homepage;
